import React, { useState, useEffect } from "react";
import { isNullOrUndefined } from "src/utils/objects";

interface RangeSelectorProps {
  options?: string[];
  value?: [number | undefined, number | undefined];
  onChange?: (value: [number | undefined, number | undefined]) => void;
  showAny?: boolean;
}

const RangeSelector = ({ 
  options = ["1", "2", "3", "4", "5+"],
  value,
  onChange,
  showAny = true
}: RangeSelectorProps) => {
  const [selection, setSelection] = useState<string | { min: number; max: number }>("any");

  const allOptions = showAny ? ["any", ...options] : options;

  // Sync internal state with external value prop
  useEffect(() => {
    if (value) {
      const [min, max] = value;
      if (isNullOrUndefined(min) && isNullOrUndefined(max)) {
        setSelection("any");
      } else if (min === max && !isNullOrUndefined(min)) {
        // Check if it's a "5+" case
        const maxOption = parseInt(options[options.length - 1]);
        if (min >= maxOption && options[options.length - 1].includes('+')) {
          setSelection(options[options.length - 1]);
        } else {
          setSelection(min.toString());
        }
      } else if (!isNullOrUndefined(min) && !isNullOrUndefined(max)) {
        setSelection({ min, max });
      }
    }
  }, [value, options]);

  // Convert selection to range values
  const selectionToRange = (sel: string | { min: number; max: number }): [number | undefined, number | undefined] => {
    if (sel === "any") {
      return [undefined, undefined];
    } else if (typeof sel === "string") {
      const num = parseInt(sel);
      return [num, num];
    } else {
      return [sel.min, sel.max];
    }
  };

  const handleSelection = (clickedValue: string) => {
    let newSelection: string | { min: number; max: number };
    
    if (clickedValue === "any") {
      newSelection = "any";
    } else if (selection === "any") {
      // If "Any" is selected and a number is pressed, select that number
      newSelection = clickedValue;
    } else if (typeof selection === "string" && selection !== "any") {
      // If it's a single number selection
      if (selection === clickedValue) {
        // Clicking the same number does nothing for single selections
        return;
      }
      // Create a range between the current selection and new value
      const current = parseInt(selection);
      const target = parseInt(clickedValue);
      const min = Math.min(current, target);
      const max = Math.max(current, target);
      newSelection = { min, max };
    } else if (typeof selection === "object") {
      // If it's a range selection
      const { min, max } = selection;
      const target = parseInt(clickedValue);

      // If clicking on min or max of current range, collapse to single number
      if (target === min || target === max) {
        newSelection = clickedValue;
      } else if (target > min && target < max) {
        // If target is within range, shrink the range
        if (target - min < max - target) {
          // Closer to min, shrink from max side
          newSelection = { min, max: target };
        } else {
          // Closer to max, shrink from min side
          newSelection = { min: target, max };
        }
      } else if (target < min) {
        // If target is outside range, expand the range
        newSelection = { min: target, max };
      } else {
        newSelection = { min, max: target };
      }
    } else {
      return;
    }

    setSelection(newSelection);
    onChange?.(selectionToRange(newSelection));
  };

  const isSelected = (value: string) => {
    if (selection === "any") {
      return value === "any";
    }

    if (typeof selection === "string") {
      return selection === value;
    }

    if (typeof selection === "object") {
      if (value === "any") return false;
      const target = parseInt(value);
      return target >= selection.min && target <= selection.max;
    }

    return false;
  };

  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden">
      {allOptions.map((option, index) => {
        const isCurrentSelected = isSelected(option);
        const isNextSelected = index < allOptions.length - 1 ? isSelected(allOptions[index + 1]) : false;
        const isPreviousSelected = index > 0 ? isSelected(allOptions[index - 1]) : false;
        
        // Determine which borders should be orange
        const isFirstInRange = isCurrentSelected && !isPreviousSelected;
        const isLastInRange = isCurrentSelected && !isNextSelected;
        const isOnlyInRange = isCurrentSelected && !isPreviousSelected && !isNextSelected;
        
        return (
          <button
            key={option}
            onClick={() => handleSelection(option)}
            className={[
              "px-4 py-2 text-sm text-muted-foreground font-medium transition-colors relative",
              "hover:bg-white focus:outline-none",
              isCurrentSelected
                ? "bg-primary-light text-orange-500"
                : "bg-white text-gray-700",
              // Orange borders for selected range
              isCurrentSelected && "border-t border-b border-orange-500",
              isFirstInRange && "border-l border-orange-500 rounded-l-lg",
              isLastInRange && "border-r border-orange-500 rounded-r-lg",
              isOnlyInRange && "border border-orange-500 rounded-lg",
              // Vertical divider after "Any" button (shorter than full height, transparent when selected)
              option === "any" && `after:content-[''] after:absolute after:-right-1 after:top-1/2 after:-translate-y-1/2 after:w-px after:h-6 ${isCurrentSelected ? 'after:bg-transparent' : 'after:bg-gray-300'}`,
              // Add left margin to the button following "Any"
              index === 1 && "ml-2",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {option === "any" ? "Any" : option}
          </button>
        );
      })}
    </div>
  );
};

export default RangeSelector;
